import React from 'react';
import '../App.css';
import MyAppBar from '../controller/appbar';

// MUI Icons for services
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import GroupsIcon from '@mui/icons-material/Groups';
import CalculateIcon from '@mui/icons-material/Calculate';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';

// MUI Icons for trust section
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HandshakeIcon from '@mui/icons-material/Handshake';

const services = [
    { name: 'Bank Reconciliations', icon: AccountBalanceIcon },
    { name: 'Accounts Payable', icon: ReceiptLongIcon },
    { name: 'Accounts Receivable', icon: PaymentsIcon },
    { name: 'Payroll & Payroll Taxes', icon: GroupsIcon },
    { name: 'Sales & Use Taxes', icon: CalculateIcon },
    { name: 'Income Tax Quarterly Payments', icon: CalendarMonthIcon },
    { name: 'Financial Reports', icon: AssessmentIcon },
    { name: 'QuickBooks Professional Advisor', icon: VerifiedIcon },
    { name: 'QuickBooks Training', icon: SchoolIcon },
];

function HomePage() {
    return (
        <div className="App">
            <MyAppBar />

            {/* ── Hero ── */}
            <header className="App-header" id="header">
                <h1 className="hero-tagline">
                    Behind every great business is a <span className="highlight">great bookkeeper.</span>
                </h1>
                <p className="hero-subtitle">
                    Let me focus on your bookkeeping so you can focus on your business.
                </p>
                <a href="mailto:danielle@dckbookkeeping.com" className="hero-cta">Get in Touch</a>
                <div className="hero-badges">
                    <img src="images/quickbooks.png" alt="QuickBooks Certified ProAdvisor" />
                    <img src="images/aipb.png" alt="American Institute of Professional Bookkeepers Certified" />
                </div>
            </header>

            <main>
                {/* ── Services ── */}
                <section className="section" id="features" aria-label="Services">
                    <p className="section-label">What I Do</p>
                    <h2 className="section-title">Services</h2>
                    <p className="section-subtitle">
                        Reliable, professional bookkeeping services in Midland, Michigan tailored to meet your business needs.
                    </p>
                    <div className="services-grid">
                        {services.map((s) => (
                            <div className="service-card" key={s.name}>
                                <div className="service-icon">
                                    <s.icon fontSize="medium" />
                                </div>
                                <div className="service-name">{s.name}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <div className="section-divider" />

                {/* ── Why DCK ── */}
                <section className="section" aria-label="Why choose DCK Bookkeeping">
                    <p className="section-label">Why DCK</p>
                    <h2 className="section-title">Built on Trust & Experience</h2>
                    <p className="section-subtitle">
                        When you work with DCK Bookkeeping, you get a dedicated professional who treats your business like her own.
                    </p>
                    <div className="trust-grid">
                        <div className="trust-item">
                            <div className="trust-icon">
                                <WorkHistoryIcon fontSize="medium" />
                            </div>
                            <p className="trust-title">25+ Years of Experience</p>
                            <p className="trust-desc">
                                Decades of hands-on bookkeeping expertise for businesses of all sizes across Mid-Michigan.
                            </p>
                        </div>
                        <div className="trust-item">
                            <div className="trust-icon">
                                <LocationOnIcon fontSize="medium" />
                            </div>
                            <p className="trust-title">Local to Midland, MI</p>
                            <p className="trust-desc">
                                A trusted member of the Midland community, proudly serving local businesses and their unique needs.
                            </p>
                        </div>
                        <div className="trust-item">
                            <div className="trust-icon">
                                <HandshakeIcon fontSize="medium" />
                            </div>
                            <p className="trust-title">Certified & Qualified</p>
                            <p className="trust-desc">
                                AIPB Certified Bookkeeper and QuickBooks ProAdvisor — credentials you can count on.
                            </p>
                        </div>
                    </div>
                </section>

                <div className="section-divider" />

                {/* ── About ── */}
                <section className="section" id="about" aria-label="About Danielle Bessemer">
                    <p className="section-label">About</p>
                    <h2 className="section-title">Meet Danielle</h2>
                    <div className="about-card">
                        <img
                            src="images/danielle.png"
                            alt="Danielle Bessemer, professional bookkeeper in Midland, Michigan"
                            className="about-photo"
                        />
                        <h3 className="about-name">Danielle Bessemer</h3>
                        <p className="about-role">Owner, DCK Bookkeeping Services Inc</p>
                        <p className="about-bio">
                            With over 25 years of experience, I have dedicated my career to helping
                            small and mid-sized businesses in the Mid-Michigan area keep their finances
                            accurate and organized. As a Certified Bookkeeper through the AIPB and a
                            QuickBooks ProAdvisor, I bring both the knowledge and the personal attention
                            your business deserves. Whether you need help with payroll, tax filings, or
                            day-to-day bookkeeping, I'm here to make your life easier.
                        </p>
                        <div className="about-links">
                            <a
                                href="https://www.ourmidland.com/news/article/dck-bookkeeping-services-midland-keeps-diverse-19597046.php"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="about-link about-link-news"
                            >
                                Featured in Midland Daily News
                            </a>
                            <a
                                href="https://www.linkedin.com/in/danielle-bessemer-47560a47/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="about-link about-link-linkedin"
                            >
                                LinkedIn
                            </a>
                        </div>
                    </div>
                </section>

                <div className="section-divider" />

                <footer className="site-footer">
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        <a href="/privacy.html">Privacy Policy</a>
                        <a href="https://fluenik.com" target="_blank" rel="noopener noreferrer">
                            Website developed by Fluenik
                        </a>
                    </div>
                </footer>
            </main>
        </div>
    );
}

export default HomePage;
